import React, {Fragment, useEffect, useState} from 'react';
import {NavLink, useNavigate, useParams, Outlet} from 'react-router-dom';
import {
    Badge,
    Button,
    Modal,
    ModalBody, ModalFooter,
    ModalHeader,
} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {deleteMovie, fetchMovie, fetchMovies, updateMovie} from '../../../slices/movies.slice';
import {fetchCrewMembers} from '../../../slices/crew.slice';
import {imgUrl} from '../../../utils/api-request';
import YoutubeTrailer from '../../../components/YoutubeTrailer';
import CrewCard from '../../../components/CrewCard';
import AddCrewMember from '../../../components/CrewCard/AddCrewMember';
import EditModal from '../../../components/EditModal';
import VersionList from '../VersionList';
import genresList from '../genres.json';
import DeleteButton from '../../../components/DeleteButton';
import '../MoviePage.scss';

const hashTitle = (title) => {
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
        hash = ((hash << 5) - hash) + title.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash);
};

const generatePattern = (title) => {
    const hash = hashTitle(title);
    const hue1 = hash % 360;
    const hue2 = (hash >> 8) % 360;
    const x1 = 10 + (hash % 30);
    const y1 = 10 + ((hash >> 4) % 40);
    const x2 = 60 + ((hash >> 8) % 30);
    const y2 = 50 + ((hash >> 12) % 40);

    const angle = (hash >> 16) % 180;
    const circleX = 70 + ((hash >> 6) % 20);
    const circleY = 20 + ((hash >> 10) % 30);
    const circleX2 = 15 + ((hash >> 14) % 25);
    const circleY2 = 60 + ((hash >> 18) % 30);

    return `
        linear-gradient(${angle}deg, transparent 48%, hsla(${hue1}, 50%, 40%, 0.08) 49%, hsla(${hue1}, 50%, 40%, 0.08) 51%, transparent 52%),
        radial-gradient(circle at ${circleX}% ${circleY}%, hsla(${hue2}, 60%, 50%, 0.1) 0%, hsla(${hue2}, 60%, 50%, 0.05) 8%, transparent 12%),
        radial-gradient(circle at ${circleX2}% ${circleY2}%, hsla(${hue1}, 70%, 45%, 0.08) 0%, transparent 10%),
        radial-gradient(ellipse at ${x1}% ${y1}%, hsla(${hue1}, 70%, 50%, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at ${x2}% ${y2}%, hsla(${hue2}, 60%, 40%, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 50%, rgba(30, 30, 50, 1) 0%, #0f0f0f 100%)
    `;
};

const MoviePageBAC = () => {
    const {movieId} = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const crew = useSelector((state) => state.crew.list);
    const crewLoadStatus = useSelector((state) => state.crew.status);
    const [modalTrailer, setModalTrailer] = useState(false);
    const currentUser = useSelector(state => state.auth.user);
    const {movie, movieStatus} = useSelector(state => state.movies);
    const [publishModal, setPublishModal] = useState(false);
    const rolesList = ['Actor', 'Director', 'Producer', 'Writer'];
    const [editModal, setEditModal] = useState({
        open: false,
        field: '',
        dataType: 'text',
        label: '',
        value: '',
        list: [],
    });

    const toggleTrailer = () => {
        setModalTrailer(!modalTrailer)
    };
    const togglePublish = () => setPublishModal(!publishModal);
    const toggleEditModal = (field, dataType, label, value, list) => {
        setEditModal({
            open: true,
            field,
            dataType,
            label,
            value,
            list,
        });
    };

    useEffect(() => {
        dispatch(fetchMovie({movieId}));
        dispatch(fetchCrewMembers());
    }, [dispatch, movieId]);

    const handleImageUploadClick = type => e => {
        e.preventDefault();
        if (!['still', 'poster', 'logo'].includes(type)) throw new Error(`${type} is invalid`)
        toggleEditModal(type, 'image', type, movie[type]?.imageUrl, true);
    };

    const handleAdd = type => selectedMembers => {
        const credits = selectedMembers.map(member => ({
            member,
            type,
            roles: type === 'cast' ? ['Actor'] : [],
            roleName: '',
        }));
        const updatedMovie = {...movie, credits: [...movie.credits, ...credits]};
        const promise = dispatch(updateMovie({movieId, body: updatedMovie})).unwrap();
        toast.promise(promise, {
            pending: 'Saving...',
            success: 'Saved successfully',
            error: 'Error saving',
        });
    };

    const handleEdit = editedMember => {
        const creditIndex = movie.credits.findIndex(credit => credit._id === editedMember._id);
        let editedMovie = structuredClone(movie);
        editedMovie.credits[creditIndex].roles = editedMember.roles;
        editedMovie.credits[creditIndex].roleName = editedMember.roleName;
        const promise = dispatch(updateMovie({movieId, body: editedMovie})).unwrap();
        toast.promise(promise, {
            pending: 'Saving...',
            success: 'Saved successfully',
            error: 'Error saving',
        });
    };

    const handleRemove = removedMemberId => {
        const filteredCredits = movie.credits.filter(credit => credit._id !== removedMemberId);
        const updatedMovie = {
            ...movie,
            credits: filteredCredits,
        };
        const promise = dispatch(updateMovie({movieId, body: updatedMovie})).unwrap();
        toast.promise(promise, {
            pending: 'Saving...',
            success: 'Saved successfully',
            error: 'Error saving',
        });
    };

    const onSaveEditModal = (newValue) => {
        let updatedMovie = {
            ...movie,
        };

        if (['still', 'poster', 'logo'].includes(editModal.field)) {
            updatedMovie[editModal.field] = newValue._id;
        } else {
            updatedMovie[editModal.field] = newValue;
        }

        const promise = dispatch(updateMovie({movieId, body: updatedMovie})).unwrap();
        toast.promise(promise, {
            pending: `Updating ${editModal.label}...`,
            success: `${editModal.label} updated`,
            error: `Error updating ${editModal.label} field`,
        });

        promise.then(() => {
            setEditModal(prev => ({...prev, open: false}));
        });
    };

    const onSaveTrailer = (trailerURL) => {
        if (trailerURL !== undefined) {
            const isRemoving = trailerURL === null;
            const updatedMovie = {
                ...movie,
                youtubeTrailerId: trailerURL,
            };
            const promise = dispatch(updateMovie({movieId, body: updatedMovie})).unwrap();
            toast.promise(promise, {
                pending: isRemoving ? 'Removing trailer...' : 'Saving...',
                success: isRemoving ? 'Trailer removed successfully' : 'Saved successfully',
                error: isRemoving ? 'Error removing trailer' : 'Error saving',
            });
            promise.then(() => toggleTrailer());
        }
    };

    const publishMovie = () => {
        const updatedMovie = {
            ...movie,
            status: 'published',
        };
        const promise = dispatch(updateMovie({movieId, body: updatedMovie})).unwrap();
        toast.promise(promise, {
            pending: `Publishing ${movie.title}...`,
            success: `${movie.title} published successfully`,
            error: `Error publishing ${movie.title}`,
        });
        promise.then(() => {
            togglePublish();
            dispatch(fetchMovies());
        });
    };

    const handleMovieDelete = async () => {
        const promise = dispatch(deleteMovie({movieId: movie._id})).unwrap();
        toast.promise(promise, {
            pending: 'Deleting movie',
            success: 'Movie and versions deleted successfully',
            error: 'Error deleting the movie',
        });
        await promise;
        navigate('/movies');
    };

    // Loading state
    if (movieStatus === 'loading' || !currentUser) {
        return (
            <div className="movie-detail-view bg-black min-vh-100 d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-white-50">Loading movie details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (movieStatus === 'failed') {
        return (
            <div className="movie-detail-view bg-black min-vh-100 d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <div className="mb-3">
                        <span className="fas fa-3x fa-exclamation-circle text-danger"/>
                    </div>
                    <h2 className="text-white mb-2">Unable to load movie</h2>
                    <p className="text-white-50 mb-4">Something went wrong while loading the movie details.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/movies')}>
                        <span className="fas fa-chevron-left me-2"/>
                        Back to movies
                    </button>
                </div>
            </div>
        );
    }

    return movieStatus === 'success' && currentUser && (
        <div className="movie-detail-view bg-black min-vh-100 pb-4 can-edit">

            <NavLink to="/movies" className="text-decoration-none text-primary p-3 position-absolute z-3">
                <span className="fas fa-chevron-left me-2"/>
                Movies
            </NavLink>

            <div className="highlight-film">
                <div
                    className="film-cover d-flex flex-column align-items-end position-relative"
                    style={{
                        backgroundImage: movie.still?.imageUrl
                            ? `url("${imgUrl(movie.still.imageUrl)}")`
                            : generatePattern(movie.title),
                        cursor: 'pointer',
                    }}
                    onClick={handleImageUploadClick('still')}
                    title="Click to update still image"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleImageUploadClick('still')(e);
                        }
                    }}
                    aria-label="Movie still - click to edit"
                >
                    {movie.status === 'published' && (
                        <Badge color="success" className="position-absolute top-0 end-0 m-3"
                               style={{fontSize: '0.875rem'}}>
                            <span className="fas fa-check-circle me-1"/>
                            Published
                        </Badge>
                    )}
                    {movie.status !== 'published' && (
                        <Badge color="secondary" className="position-absolute top-0 end-0 m-3"
                               style={{fontSize: '0.875rem'}}>
                            <span className="fas fa-pencil me-1"/>
                            Draft
                        </Badge>
                    )}

                    <div className="w-100 h-100 justify-content-center align-items-center update-still-info d-flex"
                         style={{opacity: 0, transition: 'opacity 0.2s ease', backgroundColor: 'rgba(0,0,0,0.6)'}}>
                        <p className="lead fw-bold text-center p-5 text-uppercase">
                            <span className="fas fa-2x fa-panorama mb-3"/>
                            <br/>
                            Upload still
                        </p>
                    </div>
                    <div className="gradient w-100 mt-auto"></div>
                </div>

                <div className="film-actions ps-3">
                    <div
                        className="film-logo position-relative"
                        onClick={handleImageUploadClick('logo')}
                        title="Click to update logo"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleImageUploadClick('logo')(e);
                            }
                        }}
                        aria-label="Movie logo - click to edit"
                        style={{cursor: 'pointer'}}
                    >
                        {movie.logo ? (
                            <img
                                alt={movie.title}
                                src={imgUrl(movie.logo.imageUrl)}
                                className="img-fluid"/>
                        ) : (
                            <h3 className="py-4 mt-5 text-primary text-center text-uppercase fw-bold">
                                <span className="fas fa-image me-2"/>
                                Upload logo
                            </h3>
                        )}
                    </div>

                    <div className="d-flex gap-4 mt-3">
                        <div
                            className="film-poster position-relative"
                            onClick={handleImageUploadClick('poster')}
                            title="Click to update poster"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleImageUploadClick('poster')(e);
                                }
                            }}
                            aria-label="Movie poster - click to edit"
                            style={{cursor: 'pointer', flex: '0 0 auto'}}
                        >
                            {movie.poster ? (
                                <img
                                    className="logo-img rounded"
                                    width={100}
                                    height={150}
                                    alt={`${movie.title} poster`}
                                    src={imgUrl(movie.poster.imageUrl)}
                                    style={{objectFit: 'cover'}}
                                />
                            ) : (
                                <div className="logo-img-placeholder d-flex align-items-center justify-content-center">
                                    <span className="movie-title-text text-center px-1">{movie.title}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex-grow-1">
                            <div className="icons pb-3">
                                <span
                                    className="release-year"
                                    onClick={() => toggleEditModal('releaseYear', 'number', 'Release Year', movie.releaseYear)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleEditModal('releaseYear', 'number', 'Release Year', movie.releaseYear);
                                        }
                                    }}
                                    style={{cursor: 'pointer'}}
                                >
                                    {movie.releaseYear}
                                    <span className="ms-2 fas fa-pencil"/>
                                </span>

                                <span className="restriction-genre-box ms-3">
                                    {movie.genres.length > 0 ? (
                                        movie.genres.map(genre => (
                                            <Badge key={genre} color="transparent"
                                                   className="text-uppercase">{genre}</Badge>
                                        ))
                                    ) : (
                                        <span className="text-white-50 fst-italic">No genres added</span>
                                    )}
                                    <Badge
                                        color="transparent"
                                        className="text-uppercase add-genres"
                                        onClick={() => toggleEditModal(
                                            'genres',
                                            'array',
                                            'Genres',
                                            movie.genres,
                                            genresList,
                                            onSaveEditModal,
                                        )}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleEditModal(
                                                    'genres',
                                                    'array',
                                                    'Genres',
                                                    movie.genres,
                                                    genresList,
                                                    onSaveEditModal,
                                                );
                                            }
                                        }}
                                        title="Click to add or edit genres"
                                        aria-label="Add or edit movie genres"
                                    >
                                        <span className="fas fa-plus"/>
                                        Genres
                                    </Badge>
                                </span>
                            </div>

                            <div className="d-flex gap-2 flex-wrap align-items-center my-3">
                                <Button
                                    color="primary"
                                    outline
                                    onClick={() => navigate(`/orders?movies=${movie.title}`)}
                                    aria-label={`View orders for ${movie.title}`}
                                >
                                    <span className="fas fa-list-alt me-2"/>
                                    <span>View orders</span>
                                </Button>

                                <Button
                                    color="primary"
                                    outline
                                    onClick={toggleTrailer}
                                    title={movie.youtubeTrailerId ? "Watch trailer" : "Add a trailer"}
                                    aria-label={movie.youtubeTrailerId ? `Watch ${movie.title} trailer` : `Add trailer for ${movie.title}`}
                                >
                                    <span className="fab fa-youtube text-danger me-2"/>
                                    <span>
                                        {movie.youtubeTrailerId ? 'Watch trailer' : 'Add trailer'}
                                    </span>
                                </Button>

                                {movie.status !== 'published' && (
                                    <Fragment>
                                        <Button
                                            color="link"
                                            className="text-decoration-none text-success"
                                            onClick={togglePublish}
                                            title="Publish this movie"
                                            aria-label={`Publish ${movie.title}`}
                                        >
                                            <span className="fas fa-cloud-upload me-2"/>
                                            <strong className="d-none d-sm-inline">PUBLISH!</strong>
                                        </Button>
                                        <Modal
                                            isOpen={publishModal}
                                            toggle={togglePublish}
                                        >
                                            <ModalHeader tag="h1">{movie.title}</ModalHeader>
                                            <ModalBody className="text-center">
                                                Are you sure you want to publish this movie?
                                            </ModalBody>
                                            <ModalFooter>
                                                <div className="d-flex mt-3 w-100 gap-3">
                                                    <Button color="primary" outline
                                                            onClick={togglePublish}
                                                            className="flex-grow-1">
                                                        <span className="fas fa-times me-2"/>
                                                        Not yet
                                                    </Button>
                                                    <Button color="success" outline onClick={publishMovie}
                                                            className="flex-grow-1">
                                                        <span className="fas fa-check me-2"/>
                                                        Yes, PUBLISH IT!
                                                    </Button>
                                                </div>
                                            </ModalFooter>
                                        </Modal>
                                    </Fragment>
                                )}
                            </div>

                            <h1
                                key="movie-title"
                                className="mt-3 movie-title"
                                onClick={() => toggleEditModal('title', 'text', 'Title', movie.title)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleEditModal('title', 'text', 'Title', movie.title);
                                    }
                                }}
                                style={{cursor: 'pointer'}}
                                title="Click to edit title"
                            >
                                {movie.title}
                                <span className="ms-2 fas fa-pencil"/>
                            </h1>
                        </div>
                    </div>
                    <p className="movie-synopsis lh-2 pt-3"
                       onClick={() => toggleEditModal('synopsis', 'textarea', 'Synopsis', movie.synopsis)}>
                        {movie.synopsis}
                        <span className="ms-2 fas fa-pencil"/>
                    </p>
                </div>
            </div>

            <div className="movie-versions-container p-3 border-top border-secondary">
                <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                    <h5 className="text-uppercase mb-0">
                        <span className="fas fa-film me-2 text-primary"/>
                        Movie versions ({movie.versions?.length || 0})
                    </h5>
                    <Button color="primary" outline onClick={() => navigate(`/movies/${movie._id}/version/new`)}>
                        <span className="fas fa-plus me-2"/>
                        New Version
                    </Button>
                </div>

                <VersionList
                    versions={movie.versions}
                    movie={movie}
                />
            </div>

            <div className="crew-cast-container p-3 border-top border-secondary">
                <div className="mb-4 bg-dark p-4 rounded bg-opacity-25">
                    <h5 className="text-uppercase mb-3">
                        <span className="fas fa-users me-2 text-primary"/>
                        Crew
                    </h5>
                    <div className="crew-container d-flex flex-wrap gap-2">
                        {movie.credits.filter(({type}) => type === 'crew').map(credit => (
                            <CrewCard
                                key={credit._id}
                                type="crew"
                                credit={credit}
                                canEdit
                                onEdit={handleEdit}
                                onRemove={handleRemove}
                                rolesList={rolesList}
                                showRole={true}
                            />
                        ))}

                        {crewLoadStatus === 'success' && (
                            <AddCrewMember
                                defaultMembers={crew}
                                type="crew"
                                alreadyAddedIds={movie.credits.map(({member}) => member._id)}
                                onAdd={handleAdd('crew')}
                            />
                        )}
                    </div>
                </div>

                <div className="mb-4 bg-dark p-4 rounded bg-opacity-25">
                    <h5 className="text-uppercase mb-3">
                        <span className="fas fa-theater-masks me-2 text-primary"/>
                        Cast
                    </h5>
                    <div className="cast-container d-flex flex-wrap gap-2">
                        {movie.credits.filter(({type}) => type === 'cast').map(credit => (
                            <CrewCard
                                key={credit._id}
                                type="cast"
                                credit={credit}
                                canEdit
                                onEdit={handleEdit}
                                onRemove={handleRemove}
                                rolesList={rolesList}
                                showRole={false}
                            />
                        ))}

                        {crewLoadStatus === 'success' && (
                            <AddCrewMember
                                defaultMembers={crew}
                                type="cast"
                                alreadyAddedIds={movie.credits.map(({member}) => member._id)}
                                onAdd={handleAdd('cast')}
                            />
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-end align-items-center mb-3">
                    <DeleteButton
                        entityLabel={
                            movie.versions?.length > 1
                                ? `movie and its ${movie.versions.length} versions`
                                : movie.versions?.length === 1
                                    ? 'movie and its version'
                                    : 'movie'
                        }
                        title={movie.title}
                        onClick={handleMovieDelete}
                        typeToConfirm
                    />
                </div>
            </div>

            <Modal isOpen={modalTrailer} toggle={toggleTrailer} size="lg" modalClassName="trailer">
                <ModalHeader toggle={toggleTrailer} className="border-0">
                    {movie.title} Trailer
                </ModalHeader>
                <ModalBody>
                    <YoutubeTrailer
                        videoId={movie.youtubeTrailerId}
                        title={movie.title}
                        value={movie.youtubeTrailerId}
                        canEdit
                        onSave={onSaveTrailer}/>
                </ModalBody>
            </Modal>

            <EditModal
                isOpen={editModal.open}
                toggle={() => setEditModal(prev => ({...prev, open: !prev.open}))}
                dataType={editModal.dataType}
                field={editModal.field}
                label={editModal.label}
                value={editModal.value}
                list={editModal.list}
                onSave={onSaveEditModal}
            />
            <Outlet />
        </div>
    )
}

export default MoviePageBAC;
